import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiShoppingBag, FiPackage, FiUsers, FiDollarSign,
  FiTrendingUp, FiArrowRight, FiClock, FiImage,
} from 'react-icons/fi';
import API from '../../api/axios';
import { formatPrice, getOrderStatusColor } from '../../utils/helpers';

const statusSteps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];

const StatCard = ({ icon: Icon, label, value, sub, color, link, trend }) => (
  <Link to={link} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start justify-between hover:shadow-md transition-all duration-200 group">
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-extrabold text-gray-800 leading-tight">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      {trend !== undefined && (
        <p className={`text-xs font-semibold mt-1.5 flex items-center gap-1 ${trend >= 0 ? 'text-green-500' : 'text-red-400'}`}>
          <FiTrendingUp size={11} />
          {trend >= 0 ? '+' : ''}{trend}% this week
        </p>
      )}
    </div>
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${color} group-hover:scale-110 transition-transform`}>
      <Icon size={20} className="text-white" />
    </div>
  </Link>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [productCount, setProductCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      API.get('/orders/stats'),
      API.get('/products?limit=1'),
      API.get('/users'),
    ]).then(([statsRes, prodRes, usersRes]) => {
      setStats(statsRes.data);
      setProductCount(prodRes.data.total);
      setUserCount(usersRes.data.length);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-gray-200 rounded-xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-gray-100 rounded-2xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-72 bg-gray-100 rounded-2xl" />
          <div className="h-72 bg-gray-100 rounded-2xl" />
        </div>
      </div>
    );
  }

  const pendingCount = stats?.recentOrders?.filter((o) => o.orderStatus === 'pending').length || 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-800">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-0.5">Welcome back — here's your store overview.</p>
        </div>
        <Link
          to="/admin/products/new"
          className="flex items-center gap-2 bg-primary-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-800 transition-colors shadow-sm"
        >
          <FiPackage size={15} /> Add Product
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={FiShoppingBag}
          label="Total Orders"
          value={stats?.totalOrders || 0}
          sub={pendingCount > 0 ? `${pendingCount} pending` : 'All handled'}
          color="bg-primary-600"
          link="/admin/orders"
        />
        <StatCard
          icon={FiDollarSign}
          label="Revenue"
          value={formatPrice(stats?.totalRevenue || 0)}
          sub="All time"
          color="bg-emerald-500"
          link="/admin/orders"
        />
        <StatCard
          icon={FiPackage}
          label="Products"
          value={productCount}
          sub="In catalog"
          color="bg-violet-500"
          link="/admin/products"
        />
        <StatCard
          icon={FiUsers}
          label="Customers"
          value={userCount}
          sub="Registered"
          color="bg-orange-500"
          link="/admin/users"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent orders */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex justify-between items-center mb-5">
            <h2 className="font-bold text-base text-gray-800">Recent Orders</h2>
            <Link to="/admin/orders" className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-semibold transition-colors">
              View all <FiArrowRight size={12} />
            </Link>
          </div>
          {stats?.recentOrders?.length > 0 ? (
            <div className="space-y-2.5">
              {stats.recentOrders.slice(0, 6).map((order) => (
                <div key={order._id} className="flex items-center justify-between p-3.5 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FiShoppingBag size={14} className="text-primary-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-gray-800 truncate">{order.orderNumber}</p>
                      <p className="text-xs text-gray-500 truncate">{order.user?.name || 'Guest'}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <span className={`badge capitalize text-xs ${getOrderStatusColor(order.orderStatus)}`}>
                      {order.orderStatus}
                    </span>
                    <p className="text-sm font-bold text-gray-800 mt-1">{formatPrice(order.total)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FiClock size={36} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm">No orders yet</p>
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Order status breakdown */}
          {stats?.statusBreakdown && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-base text-gray-800 mb-4">Order Status</h2>
              <div className="space-y-3">
                {statusSteps.map((status) => {
                  const count = stats.statusBreakdown?.[status] || 0;
                  const pct = stats.totalOrders > 0 ? Math.round((count / stats.totalOrders) * 100) : 0;
                  const colors = {
                    pending: 'bg-yellow-400',
                    confirmed: 'bg-blue-400',
                    processing: 'bg-purple-400',
                    shipped: 'bg-indigo-400',
                    delivered: 'bg-green-400',
                  };
                  return (
                    <div key={status}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="capitalize text-gray-600 font-medium">{status}</span>
                        <span className="text-gray-500">{count}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${colors[status]}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick actions */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold text-base text-gray-800 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {[
                { to: '/admin/products/new', label: 'Add New Product', icon: FiPackage, color: 'text-primary-700 bg-primary-50 hover:bg-primary-100' },
                { to: '/admin/categories', label: 'Manage Categories', icon: FiShoppingBag, color: 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100' },
                { to: '/admin/banners', label: 'Edit Banners', icon: FiImage, color: 'text-violet-700 bg-violet-50 hover:bg-violet-100' },
                { to: '/admin/orders?status=pending', label: 'Pending Orders', icon: FiClock, color: 'text-orange-700 bg-orange-50 hover:bg-orange-100' },
              ].map(({ to, label, icon: Icon, color }) => (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${color}`}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                  <FiArrowRight size={13} className="ml-auto opacity-50" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
